import { format, parseISO, subDays } from 'date-fns';
import { it } from 'date-fns/locale';
import type { DailyLog } from '../types';
import { SYMPTOM_CATEGORIES, POLLEN_CALENDAR } from '../types';

export function formatDateIT(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'dd/MM/yyyy', { locale: it });
}

export function formatDateLong(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, "EEEE d MMMM yyyy", { locale: it });
}

export function formatMonthYear(date: Date): string {
  return format(date, 'MMMM yyyy', { locale: it });
}

export function todayString(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function getVASColor(score: number): string {
  if (score <= 2) return '#4caf50';
  if (score <= 4) return '#8bc34a';
  if (score <= 5) return '#ffeb3b';
  if (score <= 6) return '#ff9800';
  if (score <= 8) return '#f44336';
  return '#c62828';
}

export function getVASLabel(score: number): string {
  if (score <= 2) return 'Bene';
  if (score <= 5) return 'Lieve';
  if (score <= 6) return 'Moderato';
  if (score <= 8) return 'Severo';
  return 'Molto severo';
}

export function getARIASeverity(score: number): string {
  if (score < 5) return 'Lieve';
  return 'Moderato-Severo';
}

export function calculateTNSS(log: DailyLog): number {
  return log.sneezing + log.runnyNose + log.congestion + log.nasalItch;
}

export function calculateTOSS(log: DailyLog): number {
  return log.itchyEyes + log.wateryEyes + log.redEyes;
}

export function calculateTotalSymptomScore(log: DailyLog): number {
  let total = 0;
  for (const symptoms of Object.values(SYMPTOM_CATEGORIES)) {
    for (const s of symptoms) {
      total += (log as any)[s] || 0;
    }
  }
  return total;
}

export function getHeatmapLevel(vasScore: number): number {
  if (vasScore === 0) return 0;
  if (vasScore <= 3) return 1;
  if (vasScore <= 5) return 2;
  if (vasScore <= 7) return 3;
  return 4;
}

export function calculateStreak(logs: DailyLog[]): number {
  if (logs.length === 0) return 0;
  const dateSet = new Set(logs.map(l => l.date));
  let streak = 0;
  let current = new Date();

  // Check if today has an entry
  const today = format(current, 'yyyy-MM-dd');
  if (!dateSet.has(today)) {
    // Check yesterday
    current = subDays(current, 1);
    if (!dateSet.has(format(current, 'yyyy-MM-dd'))) return 0;
  }

  while (dateSet.has(format(current, 'yyyy-MM-dd'))) {
    streak++;
    current = subDays(current, 1);
  }
  return streak;
}

export function getDaysUntilInsight(logCount: number): number {
  return Math.max(0, 7 - logCount);
}

export function getCurrentPollenAlerts(month: number, userAllergies: string[]): string[] {
  const alerts: string[] = [];
  for (const [pollen, calendar] of Object.entries(POLLEN_CALENDAR)) {
    const level = calendar[month];
    if (level && (level === 'HIGH' || level === 'VERY_HIGH')) {
      if (userAllergies.length === 0 || userAllergies.some(a => pollen.toLowerCase().includes(a.toLowerCase()) || a.toLowerCase().includes(pollen.toLowerCase()))) {
        alerts.push(`${pollen}: ${level === 'VERY_HIGH' ? 'MOLTO ALTO' : 'ALTO'}`);
      }
    }
  }
  return alerts;
}

export function generateInsights(logs: DailyLog[]): string[] {
  if (logs.length < 7) return [];
  const insights: string[] = [];

  // Average VAS
  const avgVAS = logs.reduce((s, l) => s + l.vasScore, 0) / logs.length;
  insights.push(`Il tuo punteggio medio VAS degli ultimi ${logs.length} giorni e' ${avgVAS.toFixed(1)}/10.`);

  // Worst symptom
  const symptomTotals: Record<string, number> = {};
  for (const category of Object.values(SYMPTOM_CATEGORIES)) {
    for (const s of category) {
      symptomTotals[s] = logs.reduce((sum, l) => sum + ((l as any)[s] || 0), 0);
    }
  }
  const worst = Object.entries(symptomTotals).sort((a, b) => b[1] - a[1])[0];
  if (worst && worst[1] > 0) {
    const { SYMPTOM_LABELS } = require('../types');
    insights.push(`Il sintomo piu' frequente e' "${SYMPTOM_LABELS[worst[0]]}" con un totale di ${worst[1]} punti.`);
  }

  // Windows open correlation
  const withWindows = logs.filter(l => l.windowsOpenSea || l.windowsOpenStreet);
  const withoutWindows = logs.filter(l => !l.windowsOpenSea && !l.windowsOpenStreet);
  if (withWindows.length >= 3 && withoutWindows.length >= 3) {
    const avgWith = withWindows.reduce((s, l) => s + l.vasScore, 0) / withWindows.length;
    const avgWithout = withoutWindows.reduce((s, l) => s + l.vasScore, 0) / withoutWindows.length;
    const diff = ((avgWith - avgWithout) / avgWithout * 100).toFixed(0);
    if (Math.abs(avgWith - avgWithout) > 1) {
      if (avgWith > avgWithout) {
        insights.push(`I sintomi peggiorano del ${diff}% con le finestre aperte.`);
      } else {
        insights.push(`I sintomi migliorano del ${Math.abs(Number(diff))}% con le finestre aperte.`);
      }
    }
  }

  // AC correlation
  const withAC = logs.filter(l => l.acOn);
  const withoutAC = logs.filter(l => !l.acOn);
  if (withAC.length >= 3 && withoutAC.length >= 3) {
    const avgWith = withAC.reduce((s, l) => s + l.vasScore, 0) / withAC.length;
    const avgWithout = withoutAC.reduce((s, l) => s + l.vasScore, 0) / withoutAC.length;
    if (avgWith < avgWithout - 1) {
      insights.push(`I sintomi migliorano quando l'aria condizionata e' accesa.`);
    }
  }

  // Sleep correlation
  const goodSleep = logs.filter(l => l.sleepHours !== null && l.sleepHours >= 7);
  const badSleep = logs.filter(l => l.sleepHours !== null && l.sleepHours < 6);
  if (goodSleep.length >= 3 && badSleep.length >= 2) {
    const avgGood = goodSleep.reduce((s, l) => s + l.vasScore, 0) / goodSleep.length;
    const avgBad = badSleep.reduce((s, l) => s + l.vasScore, 0) / badSleep.length;
    if (avgBad > avgGood + 1) {
      insights.push(`Dormire meno di 6 ore peggiora i sintomi allergici.`);
    }
  }

  return insights;
}

export function createEmptyLog(date: string): DailyLog {
  return {
    date,
    createdAt: '',
    updatedAt: '',
    vasScore: 0,
    sneezing: 0, runnyNose: 0, congestion: 0, nasalItch: 0,
    itchyEyes: 0, wateryEyes: 0, redEyes: 0,
    cough: 0, wheezing: 0, shortnessOfBreath: 0,
    urticaria: 0, itchingEczema: 0,
    fatigue: 0, headache: 0,
    affectedSleep: false, impairedActivities: false, impairedWork: false, troublesome: false,
    medicationsTaken: [],
    sleepHours: null, outdoorHours: null,
    sleepQuality: null, stressLevel: null,
    acOn: false, acMode: '',
    windowsOpenSea: false, windowsSeaDuration: null,
    windowsOpenStreet: false, windowsStreetDuration: null,
    carTravel: false, carAirMode: '', carMinutes: null,
    outdoorEnvironment: [], exerciseOutdoor: false,
    alcoholType: '', alcoholQuantity: null,
    petContact: false, petDuration: null,
    laundryOutdoor: false, cleaningDone: false, cleanedLitterBox: false, scratchedByMisu: false,
    crossReactiveFoods: [], smokingExposure: false, handledCardboard: false,
    notes: '',
    temperature: null, humidity: null, windSpeed: null, rain: null, pressure: null,
    pm25: null, uvIndex: null,
    pollenGrass: null, pollenBirch: null, pollenOlive: null,
    pollenMugwort: null, pollenRagweed: null, pollenAlder: null,
    entryTime: 'single',
  };
}
